import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import type { StoreOption } from '@/features/campaigns/api/use-campaigns';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { toggleValue } from '../model/campaign-form';
import {
  filterStoreGroups,
  groupCampaignStores,
  setStoreCodes,
  toCampaignStoreRows,
  type CampaignStoreGroup,
} from '../model/campaign-stores';

const COLLAPSE_AFTER = 40;
const CHIP_LIMIT = 12;

function countLabel(count: number): string {
  return count === 1 ? '1 loja' : `${count} lojas`;
}

function GroupBlock({
  group,
  grouped,
  open,
  onToggleOpen,
  selected,
  onChange,
}: {
  group: CampaignStoreGroup;
  grouped: boolean;
  open: boolean;
  onToggleOpen: () => void;
  selected: Set<string>;
  onChange: (codes: string[]) => void;
}) {
  const codes = group.stores.map((store) => store.code);
  const picked = codes.filter((code) => selected.has(code)).length;
  const checked = picked === 0 ? false : picked === codes.length ? true : 'indeterminate';

  return (
    <div>
      {grouped ? (
        <div className="sticky top-0 z-10 flex items-center gap-2 bg-background/95 px-1 py-1.5 backdrop-blur-sm">
          <button
            type="button"
            className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06]"
            aria-expanded={open}
            aria-label={open ? `Recolher ${group.label}` : `Expandir ${group.label}`}
            onClick={onToggleOpen}
          >
            {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          </button>
          <Checkbox
            id={`store-group-${group.key}`}
            checked={checked}
            onCheckedChange={(value) => onChange(setStoreCodes([...selected], codes, value === true))}
          />
          <Label htmlFor={`store-group-${group.key}`} className="flex min-w-0 flex-1 cursor-pointer items-baseline gap-2 font-normal">
            <span className="truncate">{group.label}</span>
            <span className="shrink-0 text-xs text-muted-foreground">
              {picked > 0 && picked < codes.length ? `${picked} de ${codes.length}` : countLabel(codes.length)}
            </span>
          </Label>
        </div>
      ) : null}
      {open ? (
        <ul className={cn('space-y-0.5', grouped && 'pb-1')}>
          {group.stores.map((store) => (
            <li key={store.code}>
              <label
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-lg px-1 py-1.5 text-sm hover:bg-foreground/[0.04]',
                  grouped && 'pl-9',
                )}
              >
                <Checkbox
                  checked={selected.has(store.code)}
                  onCheckedChange={() => onChange(toggleValue([...selected], store.code))}
                />
                <span className="min-w-0 flex-1 truncate">{store.name}</span>
                {store.city ? (
                  <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">{store.city}</span>
                ) : null}
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{store.code}</span>
              </label>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function CampaignStorePicker({
  stores,
  selected,
  onChange,
}: {
  stores: StoreOption[];
  selected: string[];
  onChange: (codes: string[]) => void;
}) {
  const [query, setQuery] = useState('');
  const [onlySelected, setOnlySelected] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [openReady, setOpenReady] = useState(false);

  const catalog = useMemo(() => groupCampaignStores(stores), [stores]);
  const names = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of toCampaignStoreRows(stores)) map.set(row.code, row.name);
    return map;
  }, [stores]);

  useEffect(() => {
    if (openReady || stores.length === 0) return;
    const expand = stores.length <= COLLAPSE_AFTER;
    const next: Record<string, boolean> = {};
    for (const group of catalog.groups) next[group.key] = expand;
    setOpen(next);
    setOpenReady(true);
  }, [catalog.groups, openReady, stores.length]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const visible = useMemo(() => {
    const filtered = filterStoreGroups(catalog.groups, query);
    if (!onlySelected) return filtered;
    return filtered
      .map((group) => ({
        ...group,
        stores: group.stores.filter((store) => selectedSet.has(store.code)),
      }))
      .filter((group) => group.stores.length > 0);
  }, [catalog.groups, onlySelected, query, selectedSet]);

  const visibleCodes = visible.flatMap((group) => group.stores.map((store) => store.code));
  const visiblePicked = visibleCodes.filter((code) => selectedSet.has(code)).length;
  const forceOpen = query.trim() !== '' || onlySelected;
  const chips = selected.slice(0, CHIP_LIMIT);
  const hiddenChips = selected.length - chips.length;

  return (
    <div className="rounded-xl border border-border">
      <div className="space-y-3 border-b border-border p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar por nome, código, cidade ou UF"
            aria-label="Buscar lojas"
            className="pl-9"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {visibleCodes.length > 0 ? (
            <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <Checkbox
                checked={
                  visiblePicked === 0 ? false : visiblePicked === visibleCodes.length ? true : 'indeterminate'
                }
                onCheckedChange={(value) => onChange(setStoreCodes(selected, visibleCodes, value === true))}
              />
              Selecionar visíveis ({visibleCodes.length})
            </label>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant={onlySelected ? 'primary' : 'outline'}
            disabled={selected.length === 0 && !onlySelected}
            onClick={() => setOnlySelected((value) => !value)}
          >
            Só selecionadas
          </Button>
          {selected.length > 0 ? (
            <Button type="button" size="sm" variant="ghost" onClick={() => onChange([])}>
              Limpar
            </Button>
          ) : null}
          <span className="ml-auto text-xs text-muted-foreground">
            {selected.length === 0 ? 'Vale em todas' : countLabel(selected.length)}
          </span>
        </div>

        {chips.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {chips.map((code) => (
              <button
                key={code}
                type="button"
                className="inline-flex max-w-full items-center gap-1 rounded-full border border-border bg-secondary px-2.5 py-0.5 text-xs text-secondary-foreground"
                aria-label={`Remover ${names.get(code) ?? code}`}
                onClick={() => onChange(toggleValue(selected, code))}
              >
                <span className="truncate">{names.get(code) ?? code}</span>
                <X className="size-3 shrink-0" />
              </button>
            ))}
            {hiddenChips > 0 ? (
              <span className="self-center text-xs text-muted-foreground">+{hiddenChips}</span>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="max-h-80 overflow-y-auto p-2">
        {visible.length === 0 ? (
          <p className="px-2 py-6 text-center text-sm text-muted-foreground">
            {query.trim() ? 'Nenhuma loja com esse termo.' : 'Nenhuma loja selecionada.'}
          </p>
        ) : (
          visible.map((group) => (
            <GroupBlock
              key={group.key}
              group={group}
              grouped={catalog.grouped}
              open={forceOpen || !catalog.grouped || open[group.key] === true}
              onToggleOpen={() => setOpen((prev) => ({ ...prev, [group.key]: !prev[group.key] }))}
              selected={selectedSet}
              onChange={onChange}
            />
          ))
        )}
      </div>
    </div>
  );
}
